import { CaretDown, TrendUp } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";

/**
 * Longitudinal evolution charts — one card per template that has ≥ 2 completions.
 * `series` is built by the parent (buildEvolutionSeries) and passed in ready.
 * Pure presentation.
 */
export default function EvolutionSection({ series, open, onToggle }) {
  if (!series || series.length === 0) return null;

  return (
    <div className="mb-8" data-testid="evolution-section">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex items-center gap-3 mb-4 w-full text-left"
        data-testid="toggle-evolution"
      >
        <CaretDown size={14} weight="bold" className={`text-[#5F758F] transition-transform ${open ? "" : "-rotate-90"}`} />
        <TrendUp size={20} weight="duotone" className="text-[#3A8FA5]" />
        <h3 className="text-xl font-bold text-[#1E3A5F]">Évolution</h3>
        <span className="bg-[#E6F1F5] text-[#3A8FA5] text-[13px] px-2.5 py-0.5 rounded-full font-bold">{series.length}</span>
        <span className="text-[13px] text-[#5F758F] italic ml-1">score absolu</span>
      </button>
      {open && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {series.map((s) => {
            const first = s.points[0].score;
            const last = s.points[s.points.length - 1].score;
            const delta = last - first;
            const trending = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
            const trendColor = trending === "up" ? "text-[#8B2A1E]" : trending === "down" ? "text-[#3A8FA5]" : "text-[#5F758F]";
            const trendBg = trending === "up" ? "bg-[#FFE0DC] border-[#EEC1BB]" : trending === "down" ? "bg-[#E6F1F5] border-[#C7DEE6]" : "bg-[#EEF3F8] border-[#E1E9F0]";
            return (
              <div key={s.template_id} className="bg-white border border-[#E1E9F0] rounded-[14px] px-5 py-4" data-testid={`evolution-${s.template_id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-[#1E3A5F] truncate">{s.title}</p>
                    <p className="text-[12px] text-[#5F758F] mt-0.5">{s.points.length} passages · du {s.points[0].dateLabel} au {s.points[s.points.length - 1].dateLabel} · sur {s.max}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-bold border ${trendBg} ${trendColor} whitespace-nowrap shrink-0`}>
                    {delta > 0 ? "+" : ""}{delta} pts
                  </span>
                </div>
                <ResponsiveContainer width="100%" height={170}>
                  <LineChart data={s.points} margin={{ top: 30, right: 24, bottom: 4, left: -20 }}>
                    <CartesianGrid stroke="#E1E9F0" strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="dateLabel"
                      tick={{ fontSize: 10, fill: "#5F758F" }}
                      tickLine={false}
                      axisLine={{ stroke: "#E1E9F0" }}
                    />
                    <YAxis
                      domain={[0, s.max]}
                      allowDecimals={false}
                      tick={{ fontSize: 10, fill: "#5F758F" }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Line
                      type="linear"
                      dataKey="score"
                      stroke="#3A8FA5"
                      strokeWidth={2.5}
                      dot={{ fill: "#3A8FA5", r: 5, strokeWidth: 2, stroke: "#FFFFFF" }}
                      activeDot={false}
                      isAnimationActive={false}
                      label={{
                        position: "top",
                        offset: 10,
                        fill: "#1E3A5F",
                        fontSize: 12,
                        fontWeight: 700,
                        fontFamily: "Manrope, sans-serif",
                      }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
